import ReactGA from 'react-ga4';

/**
 * Initialize Google Analytics 4
 */
export const initGA4 = () => {
  const measurementId = import.meta.env.VITE_GA4_MEASUREMENT_ID;
  
  if (!measurementId) {
    console.warn('GA4: Measurement ID not found in environment variables');
    return false;
  }

  try {
    ReactGA.initialize(measurementId, {
      gaOptions: {
        siteSpeedSampleRate: 100, // Sample 100% of users for site speed
      },
      gtagOptions: {
        send_page_view: false, // We'll send page views manually via React Router
      }
    });
    
    console.log('GA4 initialized successfully');
    return true;
  } catch (error) {
    console.error('GA4 initialization failed:', error);
    return false;
  }
};

/**
 * Track page view
 * @param {string} path - Page path (e.g., '/en/benchmark')
 * @param {string} title - Page title
 */
export const trackPageView = (path, title) => {
  try {
    ReactGA.send({ 
      hitType: 'pageview', 
      page: path,
      title: title 
    });
  } catch (error) {
    console.error('GA4 pageview tracking failed:', error);
  }
};

/**
 * Track custom event
 * @param {string} category - Event category (e.g., 'Benchmark')
 * @param {string} action - Event action (e.g., 'started')
 * @param {object} params - Additional parameters
 */
export const trackEvent = (category, action, params = {}) => {
  try {
    ReactGA.event({
      category,
      action,
      ...params
    });
  } catch (error) {
    console.error('GA4 event tracking failed:', error);
  }
};

/**
 * Benchmark-specific events
 */
export const BenchmarkEvents = {
  /**
   * User starts benchmark
   * @param {string} mode - 'simple' or 'advanced'
   * @param {string} device - 'cpu', 'gpu', 'npu'
   */
  started: (mode, device) => {
    trackEvent('Benchmark', 'started', {
      benchmark_mode: mode,
      device_type: device,
    });
  },

  /**
   * User completes benchmark
   * @param {number} tokensPerSecond - Performance score
   * @param {string} device - 'cpu', 'gpu', 'npu'
   * @param {string} capability - 'POWERFUL', 'MEDIUM', 'BASIC', 'LIMITED'
   * @param {number} duration - Test duration in seconds
   */
  completed: (tokensPerSecond, device, capability, duration) => {
    trackEvent('Benchmark', 'completed', {
      tokens_per_second: tokensPerSecond,
      device_type: device,
      device_capability: capability,
      test_duration: duration,
      value: tokensPerSecond, // For analytics value tracking
    });
  },

  /**
   * User stops benchmark before completion
   */
  stopped: () => {
    trackEvent('Benchmark', 'stopped');
  },

  /**
   * Benchmark encounters an error
   * @param {string} errorType - Error category
   * @param {string} errorMessage - Error description
   */
  error: (errorType, errorMessage) => {
    trackEvent('Benchmark', 'error', {
      error_type: errorType,
      error_message: errorMessage,
    });
  },

  /**
   * Model preload started
   * @param {string} device - 'cpu', 'gpu', 'npu'
   */
  preloadStarted: (device) => {
    trackEvent('Benchmark', 'preload_started', {
      device_type: device,
    });
  },

  /**
   * Model preload completed
   * @param {string} device - 'cpu', 'gpu', 'npu'
   * @param {number} loadTimeMs - Time taken to load in milliseconds
   */
  preloadCompleted: (device, loadTimeMs) => {
    trackEvent('Benchmark', 'preload_completed', {
      device_type: device,
      load_time_ms: loadTimeMs,
      load_time_seconds: (loadTimeMs / 1000).toFixed(2),
    });
  },

  /**
   * Race mode started (comparing multiple devices)
   * @param {array} devices - Array of devices being tested
   */
  raceStarted: (devices) => {
    trackEvent('Benchmark', 'race_started', {
      devices_count: devices.length,
      devices_list: devices.join(','),
    });
  },

  /**
   * View details clicked on results
   */
  viewDetails: () => {
    trackEvent('Benchmark', 'view_details_clicked');
  },
};

/**
 * Email capture events
 */
export const EmailEvents = {
  /**
   * Email modal opened
   * @param {string} appId - App ID (e.g., 'infinia_chat')
   * @param {boolean} fromBenchmark - Whether opened from benchmark results
   */
  modalOpened: (appId, fromBenchmark) => {
    trackEvent('Email', 'modal_opened', {
      app_id: appId,
      from_benchmark: fromBenchmark,
    });
  },

  /**
   * Email successfully captured
   * @param {string} appId - App ID
   * @param {boolean} consentGiven - Whether user gave consent
   */
  captured: (appId, consentGiven) => {
    trackEvent('Email', 'captured', {
      app_id: appId,
      consent_given: consentGiven,
      value: 1, // Each email capture has value
    });
  },

  /**
   * Email modal closed without submission
   */
  dismissed: () => {
    trackEvent('Email', 'dismissed');
  },
};

/**
 * App download events
 */
export const AppEvents = {
  /**
   * Download button clicked
   * @param {string} appId - App ID
   * @param {string} platform - 'android', 'windows', 'ios', etc.
   * @param {string} source - Where the click originated
   */
  downloadClicked: (appId, platform, source = 'website') => {
    trackEvent('App', 'download_clicked', {
      app_id: appId,
      platform: platform,
      source: source,
    });
  },

  /**
   * Partner modal opened
   * @param {string} partnerId - Partner identifier
   */
  partnerModalOpened: (partnerId) => {
    trackEvent('App', 'partner_modal_opened', {
      partner_id: partnerId,
    });
  },
};

/**
 * Navigation events
 */
export const NavigationEvents = {
  /**
   * Language changed
   * @param {string} fromLang - Previous language
   * @param {string} toLang - New language
   */
  languageChanged: (fromLang, toLang) => {
    trackEvent('Navigation', 'language_changed', {
      from_language: fromLang,
      to_language: toLang,
    });
  },

  /**
   * View mode toggled in benchmark
   * @param {string} mode - 'simple' or 'advanced'
   */
  viewModeToggled: (mode) => {
    trackEvent('Navigation', 'view_mode_toggled', {
      view_mode: mode,
    });
  },

  /**
   * Console opened/closed
   * @param {boolean} isOpen - Whether console is now open
   */
  consoleToggled: (isOpen) => {
    trackEvent('Navigation', 'console_toggled', {
      is_open: isOpen,
    });
  },
};

/**
 * User engagement events
 */
export const EngagementEvents = {
  /**
   * FAQ item clicked
   * @param {string} question - Question text or ID
   */
  faqClicked: (question) => {
    trackEvent('Engagement', 'faq_clicked', {
      question: question,
    });
  },

  /**
   * Social share clicked
   * @param {string} platform - 'twitter', 'facebook', 'linkedin', etc.
   */
  socialShareClicked: (platform) => {
    trackEvent('Engagement', 'social_share_clicked', {
      platform: platform,
    });
  },

  /**
   * External link clicked
   * @param {string} url - Destination URL
   * @param {string} label - Link label/description
   */
  externalLinkClicked: (url, label) => {
    trackEvent('Engagement', 'external_link_clicked', {
      url: url,
      label: label,
    });
  },
};
